// Per-tenant GymSales credential editor. Lists every fitness-industry
// domain with an inline "Override" toggle → username/password/company
// fields → "Test" and "Save" buttons. Sandbox creds keep working as the
// fallback for any domain without an override.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Loader2, Building2, Key, Search, Save, Eye, EyeOff,
} from 'lucide-react';
import { API } from './shared';

const TenantConfigsCard = ({ headers, onSaved }) => {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [edit, setEdit] = useState({});           // { [domain_id]: {username,password,company_id,base_url,enabled} }
  const [busy, setBusy] = useState({});           // { [domain_id]: 'save'|'test' }
  const [showPwd, setShowPwd] = useState({});
  const [testResults, setTestResults] = useState({});

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await axios.get(`${API}/admin/gymsales/tenant-configs`, { headers });
      setRows(r.data.domains || []);
    } catch (e) { setErr(e?.response?.data?.detail || 'Failed to load tenant configs.'); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => {
    const c = row.creds || {};
    setEdit((s) => ({
      ...s,
      [row.domain_id]: {
        enabled: c.source === 'domain' ? c.enabled : false,
        api_username: c.source === 'domain' ? c.api_username : '',
        api_password: '',      // never prefilled
        company_id: c.source === 'domain' ? c.company_id : '',
        base_url: c.source === 'domain' ? c.base_url : '',
      },
    }));
    setExpandedId(row.domain_id);
  };

  const save = async (row) => {
    const draft = edit[row.domain_id];
    if (!draft) return;
    setBusy((s) => ({ ...s, [row.domain_id]: 'save' }));
    try {
      await axios.patch(
        `${API}/admin/gymsales/tenant-configs/${row.domain_id}`,
        draft, { headers },
      );
      await load();
      setExpandedId(null);
      if (onSaved) onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || 'Save failed.'); }
    finally { setBusy((s) => { const n = { ...s }; delete n[row.domain_id]; return n; }); }
  };

  const test = async (row) => {
    const draft = edit[row.domain_id] || {};
    setBusy((s) => ({ ...s, [row.domain_id]: 'test' }));
    setTestResults((s) => ({ ...s, [row.domain_id]: null }));
    try {
      const r = await axios.post(
        `${API}/admin/gymsales/tenant-configs/${row.domain_id}/test`,
        {
          api_username: draft.api_username || undefined,
          api_password: draft.api_password || undefined,
          base_url: draft.base_url || undefined,
        },
        { headers },
      );
      setTestResults((s) => ({ ...s, [row.domain_id]: r.data }));
    } catch (e) {
      setTestResults((s) => ({ ...s, [row.domain_id]: { ok: false, error: e?.response?.data?.detail || 'Test failed.' } }));
    } finally { setBusy((s) => { const n = { ...s }; delete n[row.domain_id]; return n; }); }
  };

  if (rows === null) return <div className="text-white/40 text-xs italic p-4"><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Loading tenant configs…</div>;
  if (rows.length === 0) return <div className="text-white/40 text-xs italic p-4">No fitness-industry domains configured. Set a domain&apos;s <code className="text-white/60">industry</code> to <code className="text-white/60">fitness_gym</code> to enable tenant creds.</div>;

  return (
    <div className="divide-y divide-white/5" data-testid="gymsales-tenant-configs">
      {err && <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5 m-3">{err}</div>}
      {rows.map((row) => {
        const source = row.creds?.source;
        const isExpanded = expandedId === row.domain_id;
        const draft = edit[row.domain_id] || {};
        const busyKind = busy[row.domain_id];
        const result = testResults[row.domain_id];
        return (
          <div key={row.domain_id} className="p-3" data-testid={`tenant-config-row-${row.domain_id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Building2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-white font-semibold text-sm truncate">{row.name}</div>
                  <div className="text-[11px] text-white/50 font-mono truncate">{row.creds?.api_username || '—'} · company {row.creds?.company_id || '—'}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-semibold ${
                  source === 'domain'
                    ? 'bg-teal-500/20 text-teal-300 border-teal-500/40'
                    : 'bg-slate-500/20 text-slate-300 border-slate-500/40'
                }`}>
                  {source === 'domain' ? 'Tenant creds' : 'Shared sandbox'}
                </span>
                <button
                  type="button"
                  onClick={() => (isExpanded ? setExpandedId(null) : startEdit(row))}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white/80"
                  data-testid={`tenant-config-edit-${row.domain_id}`}
                >
                  <Key className="w-3 h-3" />{isExpanded ? 'Cancel' : (source === 'domain' ? 'Edit' : 'Override')}
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <label className="flex items-center gap-2 col-span-full">
                  <input
                    type="checkbox"
                    checked={!!draft.enabled}
                    onChange={(e) => setEdit((s) => ({ ...s, [row.domain_id]: { ...draft, enabled: e.target.checked } }))}
                    data-testid={`tenant-config-enabled-${row.domain_id}`}
                  />
                  <span className="text-white/80">Use tenant credentials (uncheck to revert to shared sandbox)</span>
                </label>
                <input
                  type="text"
                  placeholder="api_username (email)"
                  value={draft.api_username || ''}
                  onChange={(e) => setEdit((s) => ({ ...s, [row.domain_id]: { ...draft, api_username: e.target.value } }))}
                  className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white placeholder-white/30 font-mono text-[11px]"
                  data-testid={`tenant-config-username-${row.domain_id}`}
                />
                <div className="relative">
                  <input
                    type={showPwd[row.domain_id] ? 'text' : 'password'}
                    placeholder={source === 'domain' ? 'api_password (leave blank to keep)' : 'api_password'}
                    value={draft.api_password || ''}
                    onChange={(e) => setEdit((s) => ({ ...s, [row.domain_id]: { ...draft, api_password: e.target.value } }))}
                    className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 pr-8 text-white placeholder-white/30 font-mono text-[11px]"
                    data-testid={`tenant-config-password-${row.domain_id}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => ({ ...s, [row.domain_id]: !s[row.domain_id] }))}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80"
                  >
                    {showPwd[row.domain_id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="company_id"
                  value={draft.company_id || ''}
                  onChange={(e) => setEdit((s) => ({ ...s, [row.domain_id]: { ...draft, company_id: e.target.value } }))}
                  className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white placeholder-white/30 font-mono text-[11px]"
                  data-testid={`tenant-config-company-${row.domain_id}`}
                />
                <input
                  type="text"
                  placeholder="base_url (optional, defaults to sandbox)"
                  value={draft.base_url || ''}
                  onChange={(e) => setEdit((s) => ({ ...s, [row.domain_id]: { ...draft, base_url: e.target.value } }))}
                  className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white placeholder-white/30 font-mono text-[11px]"
                />

                {result && (
                  <div className={`col-span-full text-[11px] rounded px-2 py-1.5 border ${result.ok ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200' : 'bg-rose-500/10 border-rose-500/30 text-rose-200'}`} data-testid={`tenant-config-test-result-${row.domain_id}`}>
                    {result.ok ? (
                      <>✓ {result.status_code} in {Math.round(result.latency_ms)}ms — {(result.companies?.length || 0) > 1 ? 'clubs' : 'company'}: <b>{(result.companies?.length ? result.companies.map((c) => `${c.name} (${c.id})`).join(', ') : `${result.company?.name || '—'} (id ${result.company?.id || '—'})`)}</b></>
                    ) : (
                      <div className="space-y-1">
                        <div>✗ {result.problem || result.error || 'Test failed'}</div>
                        {result.router_request_id && (
                          <div className="text-[10px] font-mono text-rose-200/70 flex items-center gap-1.5">
                            request-id:
                            <code className="bg-black/30 px-1 py-0.5 rounded select-all" data-testid={`tenant-config-request-id-${row.domain_id}`}>{result.router_request_id}</code>
                            <button
                              type="button"
                              onClick={() => navigator.clipboard?.writeText(result.router_request_id)}
                              className="text-rose-200/80 hover:text-rose-100 underline underline-offset-2"
                              data-testid={`tenant-config-copy-request-id-${row.domain_id}`}
                            >
                              copy
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="col-span-full flex items-center gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => test(row)}
                    disabled={busyKind}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white/80 disabled:opacity-50"
                    data-testid={`tenant-config-test-${row.domain_id}`}
                  >
                    {busyKind === 'test' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}Test
                  </button>
                  <button
                    type="button"
                    onClick={() => save(row)}
                    disabled={busyKind}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-semibold text-xs"
                    data-testid={`tenant-config-save-${row.domain_id}`}
                  >
                    {busyKind === 'save' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}Save
                  </button>
                  <span className="text-white/40 text-[10px] italic ml-auto">
                    {row.creds?.updated_at ? `updated ${new Date(row.creds.updated_at).toLocaleDateString()}` : 'no override saved'}
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TenantConfigsCard;
